import { Component, ElementRef, Input, QueryList, ViewChild, ViewChildren, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { TimeService } from '@services/time-service';
import { FollowUpReport } from '../app-constant-types';
import { AppConfig } from '../app.config';
import { UserAllocatedDoctorService } from '@services/user-allocated-doctor-service/user-allocated-doctor.service';

@Component({
  selector: 'user-followup-report',
  templateUrl: './user-followup-report.html',
  styleUrls: ['./user-followup-report.scss'],
  standalone: false,
})

export class UserFollowupReportComponent implements OnInit {
  loading: boolean = true;
  username: string;
  user: any;
  instantCheckups: Array<any> = [];
  hideTitle: boolean = false;
  followupReport: any;
  report: FollowUpReport = { imageConfig: { after: {}, before: {} } };
  comparisonSliders: any[];
  animateProgressBar: boolean = false;
  throttle: boolean;
  interval: any;
  progressBarStages: any[];
  reportStagesLanguageStringId: string[] = Object.values(this.appConfig.Shared.FollowUpReport.Stages);
  isInternalUser: boolean;
  isHelpfull: boolean;
  afterSaveFeedback: boolean;
  feedback: any;
  showFeedbackQuestions: boolean;
  iAmUselessVariable: boolean = false;
  doctorInfo: { name: string; imageUrl: string };
  followUpCount: number;

  @ViewChild('audio', { static: false }) audio: ElementRef;
  // eslint-disable-next-line new-cap
  @ViewChildren('scrollContainer') scrollContainer: QueryList<ElementRef>;
  // eslint-disable-next-line new-cap
  @ViewChildren('progressBar') progressBar: QueryList<ElementRef>;
  // eslint-disable-next-line new-cap
  @ViewChildren('feedbackSection') feedbackSection: QueryList<ElementRef>;
  @Input('assistantId') assistantId: any;

  constructor(private conn: ConnectionService,
    private readonly broadcast: BroadcastService,
    public readonly route: ActivatedRoute,
    public readonly appConfig: AppConfig,
    private readonly eventLogger: EventLoggerService,
    private readonly timeService: TimeService,
    private readonly doctorService: UserAllocatedDoctorService) {
  }

  async ngOnInit(): Promise<any> {
    this.hideTitle = !!this.assistantId;
    this.isInternalUser = this.conn.isInternalUser();
    this.user = await this.conn.getActingUser();
    this.username = this.user?.get('username');
    await this.loadDoctorInfo();
    try {
      await this.loadDataForV2();
    } catch (error) {
      this.showError(error.message);
      this.broadcast.broadcast('NAVIGATION_BACK');
    }
    this.loading = false;
    if (!this.report.trackingDetails) {
      this.report.trackingDetails = {};
    }
  }

  /**
   * v2 - In version 2, the data is store in FollowupReport table.
   * so params.id is followup report id. We fetch the data and process it.
   */
  async loadDataForV2(): Promise<any> {
    const followUpReportId = this.route.snapshot.params.id;
    this.followupReport = await this.conn.fetchFollowUpReportById(followUpReportId);
    this.followUpCount = this.followupReport.get('followUp').get('followUpCount');
    this.report = JSON.parse(JSON.stringify(this.followupReport));
    this.feedback = this.report.feedback || {};
    const baseFeedbackIncomplete = (this.feedback?.isReportSatisfied === undefined)
      || (this.feedback?.isVoiceNoteUnderstandable === undefined);
    const feedbackIncomplete = this.followUpCount === 1
      ? baseFeedbackIncomplete
      : baseFeedbackIncomplete || (this.feedback?.doubtsCleared === undefined);
    if (feedbackIncomplete
      && this.timeService.differenceInDays(new Date(), new Date(this.report.createdAt)) <= 7) {
      this.showFeedbackQuestions = true;
    }
    if (this.report.secondaryComparisons) {
      this.comparisonSliders = [this.followupReport, ...this.report.secondaryComparisons];
    } else {
      this.comparisonSliders = [this.followupReport];
    }
    await this.fetchProgressBarStageStrings();
    const interval = setInterval((): void => {
      if (this.scrollContainer.first) {
        this.scrollContainer.first.nativeElement.addEventListener('scroll', this.scrollListener());
        clearInterval(interval);
      }
    }, 150);
  }

  /**
   * These are language strings of progress bar stage.
   * eg: Excellent, Good, Bad, ...
   * we fetch the string by id stored in shared file and sort to get the result in same order.
   */
  async fetchProgressBarStageStrings(): Promise<void> {
    if (!this.report.isImageOnlyReport) {
      const stagesLanguageId = this.reportStagesLanguageStringId;
      const stages = await this.conn.getLanguageStringsById(stagesLanguageId);
      const stagesSorted = [];
      stagesLanguageId.forEach((id: string): void => {
        stages.forEach((each: any): void => {
          if (each.id === id) stagesSorted.push(each);
        });
      });
      this.progressBarStages = stagesSorted;
    }
  }

  /**
   * Listens for the scroll and when the treatment progress bar section is reached, it sets the flag to animate the progress.
   * Then it removes the listener. The animations lifetime is only once.
   */
  scrollListener(): (event: any) => any {
    return (event: any): void => {
      if (this.throttle && this.report.trackingDetails.scrollTillBottom) return;
      this.interval = setTimeout((): boolean => this.throttle = false, 150);
      this.throttle = true;
      const scrollContainer = event.target;
      const progressBarPosition = scrollContainer.scrollTop + scrollContainer.clientHeight;
      if (progressBarPosition >= this.progressBar?.first?.nativeElement.offsetTop) {
        this.animateProgressBar = true;
        clearInterval(this.interval);
      }
      if (progressBarPosition >= this.feedbackSection?.first?.nativeElement.offsetTop) {
        if (!this.report.trackingDetails?.scrollTillBottom) {
          this.report.trackingDetails.scrollTillBottom = true;
          this.eventLogger.cleverTapEvent('scrollTillBottom', JSON.stringify({ followupReportId: this.report.objectId,
            pageName: 'checkup' }));
          this.conn.trackVoiceNotesAndScrollEvents({ objectId: this.report.objectId,
            eventName: 'scrollTillBottom',
            trackingTable: 'followUpReport' });
        }
        event.target.removeEventListener('scroll', this.scrollListener());
      }
    };
  }

  findTypeOfReport(): string {
    let type: string;
    if ([this.appConfig.Shared.InstantCheckup.Type.FULL_FACE,
      this.appConfig.Shared.InstantCheckup.Type.SIDE_FACE,
      this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE,
      this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE,
      this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE].includes(this.instantCheckups[0].type)) {
      type = this.appConfig.Shared.Regimen.Class.FACE;
    } else if ([this.appConfig.Shared.InstantCheckup.Type.HAIR,
      this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT,
      this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP].includes(this.instantCheckups[0].type)) {
      type = this.appConfig.Shared.Regimen.Class.HAIR;
    } else type = this.appConfig.Shared.Regimen.Class.BODY;
    return type;
  }

  openConsentForm(): void {
    this.conn.navigateToURL(`/chatV2/${this.report.supportTicket?.objectId}?type=supportTicket&username=${this.username}`
    + `&reportId=${this.report.objectId}`);
  }

  viewRegimen(): void {
    this.conn.navigateToURL(`/user?tab=regimen&class=${this.report.type}&username=${this.username}`);
  }

  async onAudioPlay(): Promise<void> {
    if (this.report.trackingDetails?.audioPlayed) return;
    this.report.trackingDetails.audioPlayed = true;
    const voiceNote = this.report.objectId ? this.appConfig.Shared.questionId.checkupVoiceNote : 'undefined';
    this.eventLogger.cleverTapEvent('audioPlayed', JSON.stringify({ followupReportId: this.report.objectId, pageName: 'checkup', voiceNote }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    await this.conn.trackVoiceNotesAndScrollEvents({ objectId: this.report.objectId,
      eventName: 'audioPlayed',
      trackingTable: 'followUpReport' });
  }

  async onAudioPlayEnd(): Promise<void> {
    if (this.report?.trackingDetails?.audioFinished) return;
    this.report.trackingDetails.audioFinished = true;
    this.eventLogger.cleverTapEvent('audioFinished', JSON.stringify({ followupReportId: this.report.objectId, pageName: 'checkup' }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    await this.conn.trackVoiceNotesAndScrollEvents({ objectId: this.report.objectId,
      eventName: 'audioFinished',
      trackingTable: 'followUpReport' });
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  showError(message: any): void {
    this.broadcast.broadcast('NOTIFY', { message });
  }

  async updateFeedback(isHelpfull: boolean): Promise<void> {
    if (this.conn.isInternalUser()) return;
    this.isHelpfull = isHelpfull;
    this.followupReport.set('feedback', { feedback: isHelpfull });
    await this.followupReport.save();
    this.afterSaveFeedback = true;
  }

  async saveFeedback(key: string, value: boolean): Promise<void> {
    if (this.conn.isInternalUser()) return;
    this.feedback[key] = value;
    this.followupReport.set('feedback', this.feedback);
    try {
      await this.followupReport.save();
      const baseFeedbackComplete = (this.feedback?.isReportSatisfied !== undefined)
        && (this.feedback?.isVoiceNoteUnderstandable !== undefined);
      if (this.followUpCount === 1
        ? baseFeedbackComplete
        : baseFeedbackComplete && (this.feedback?.doubtsCleared !== undefined)) {
        this.afterSaveFeedback = true;
      }
    } catch (error) {
      this.broadcast.broadcast('NOTIFY', { message: error.message });
    }
  }

  async trackEvent(action: any): Promise<void> {
    switch (action) {
      case 'play': {
        await this.onAudioPlay();
        break;
      }
      case 'completed': {
        await this.onAudioPlayEnd();
        break;
      }
      default:
    }
  }

  async loadDoctorInfo(): Promise<void> {
    const doctor = await this.doctorService.getAllocatedDoctor(true);
    this.doctorInfo = { name: doctor.DoctorDisplayName, imageUrl: doctor.doctorDisplayImage };
  }
}
